import { Briefcase, Trophy, Clock, Euro } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { CompanyStats } from '@/modules/core/companies/types/company.types';

interface CompanySmartButtonsProps {
  stats: CompanyStats | undefined;
  isLoading?: boolean;
  onOpportunitiesClick?: () => void;
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M€`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k€`;
  return `${value.toFixed(0)}€`;
}

export function CompanySmartButtons({
  stats,
  isLoading,
  onOpportunitiesClick,
}: CompanySmartButtonsProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  const cards = [
    {
      label: 'Opportunités',
      value: stats.opportunitiesCount,
      icon: Briefcase,
      color: 'text-copper',
      bgColor: 'bg-copper/10',
      onClick: onOpportunitiesClick,
    },
    {
      label: 'Gagnées',
      value: stats.wonCount,
      icon: Trophy,
      color: 'text-green-700',
      bgColor: 'bg-green-50',
    },
    {
      label: 'En cours',
      value: stats.inProgressCount,
      icon: Clock,
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'CA potentiel',
      value: formatCurrency(stats.totalEstimatedValue),
      icon: Euro,
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        const clickable = !!card.onClick;
        return (
          <Card
            key={card.label}
            className={`p-4 ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            onClick={card.onClick}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <Icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{card.label}</p>
                <p className="text-xl font-bold truncate">{card.value}</p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
